import type { SensorStation } from '@/lib/mqtt/schemas/surveillance';
import type { RfDetails } from '@/lib/mqtt/schemas/targets';
/**
 * RF drone detection adapter — `v1/ingest/live_rf/*` (ENG-253).
 *
 * Envelope on every message: `{ key: <sensor SN>, ts: <unix s>, body }`.
 *   • REAL_TIME_DATA — `body.palerts[]` drones WITH a position fix become
 *     targets; `body.module_locations[]` become sensor stations.
 *     `body.alerts[]` (direction only, no GPS) is deliberately ignored.
 *   • HEARTBEAT      — `body: "heartbeat"`, liveness only.
 *   • INBOUND_DATA   — raw targets with mostly-zero locations; not used.
 *
 * Units are assumed, not confirmed by the sensor vendor: `speed` in m/s,
 * `altitude` in metres MSL, `height` in metres AGL.
 */
import { bearing as turfBearing } from '@turf/turf';
import { z } from 'zod';
import type { EmitterSnapshot } from './surveillanceSensors';
import type { PartialTarget } from './surveillanceTraffic';

export const LIVE_RF_EMITTER = 'live_drone_rf';
export const LIVE_RF_TOPIC_PREFIX = 'v1/ingest/live_rf/';

const M_TO_FT = 3.28084;
const MS_TO_KTS = 1.943844;

const Envelope = z.object({
  key: z.string(),
  ts: z.number().nullish(),
  body: z.unknown(),
});

const LatLon = z.object({ lat: z.number(), lon: z.number() });

const PAlert = z.object({
  serial: z.string(),
  sn: z.string().nullish(),
  model: z.string().nullish(),
  freq: z.number().nullish(),
  rssi: z.number().nullish(),
  height: z.number().nullish(),
  altitude: z.number().nullish(),
  speed: z.number().nullish(),
  drone_gps: LatLon.nullish(),
  rc_gps: LatLon.nullish(),
  return_positioning: LatLon.nullish(),
  drone_trajectory: z.array(LatLon.extend({ height: z.number().nullish() })).nullish(),
});

const ModuleLocation = z.object({
  sn: z.string(),
  name: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  status: z
    .object({
      status: z.string().nullish(),
      lat: z.number().nullish(),
      lng: z.number().nullish(),
    })
    .nullish(),
});

const Body = z.object({
  palerts: z.array(z.unknown()).nullish(),
  module_locations: z.array(z.unknown()).nullish(),
});

/** The sensor reports 0/0 for "no fix". */
function validFix(
  p: { lat: number; lon: number } | null | undefined,
): { lat: number; lng: number } | null {
  if (!p) return null;
  if (p.lat === 0 && p.lon === 0) return null;
  if (Math.abs(p.lat) > 90 || Math.abs(p.lon) > 180) return null;
  return { lat: p.lat, lng: p.lon };
}

export function isLiveRfTopic(topic: string): boolean {
  return topic.startsWith(LIVE_RF_TOPIC_PREFIX);
}

export function liveRfKind(topic: string): string {
  return topic.slice(LIVE_RF_TOPIC_PREFIX.length);
}

/** Sensor SN of a heartbeat, or null when the message isn't one. */
export function parseLiveRfHeartbeat(raw: unknown): string | null {
  const env = Envelope.safeParse(raw);
  if (!env.success || env.data.body !== 'heartbeat') return null;
  return env.data.key;
}

export interface LiveRfFrame {
  sensorSn: string;
  targets: PartialTarget[];
  snapshot: EmitterSnapshot;
}

export function parseLiveRfRealtime(raw: unknown, receivedAtIso: string): LiveRfFrame | null {
  const env = Envelope.safeParse(raw);
  if (!env.success) return null;
  const body = Body.safeParse(env.data.body);
  if (!body.success) return null;
  const sensorSn = env.data.key;

  const targets: PartialTarget[] = [];
  const perSensor = new Map<string, number>();
  for (const item of body.data.palerts ?? []) {
    const p = PAlert.safeParse(item);
    if (!p.success) continue;
    const a = p.data;
    const pos = validFix(a.drone_gps);
    if (!pos) continue;
    const owner = a.sn ?? sensorSn;
    perSensor.set(owner, (perSensor.get(owner) ?? 0) + 1);

    const rf: RfDetails = {
      serial: a.serial,
      model: a.model?.trim() || null,
      freq_mhz: a.freq ?? null,
      rssi_dbm: a.rssi ?? null,
      height_m: a.height ?? null,
      operator: validFix(a.rc_gps),
      home: validFix(a.return_positioning),
      sensor_sn: owner,
    };
    targets.push({
      target_id: a.serial,
      callsign: a.serial,
      icao_hex: null,
      lat: pos.lat,
      lng: pos.lng,
      heading: headingFromTrajectory(a.drone_trajectory),
      altitude_ft: Math.round((a.altitude ?? 0) * M_TO_FT),
      speed_kts: Math.round((a.speed ?? 0) * MS_TO_KTS * 10) / 10,
      type: 'light',
      trust: 'medium',
      source_services: [LIVE_RF_EMITTER],
      received_at: receivedAtIso,
      // tar1090 category B6 = UAV; picks the quadcopter silhouette.
      category: 'B6',
      aircraft_type: null,
      registration: null,
      description: null,
      rf,
    });
  }

  const stations: SensorStation[] = [];
  for (const item of body.data.module_locations ?? []) {
    const m = ModuleLocation.safeParse(item);
    if (!m.success) continue;
    const loc = validFix({
      lat: m.data.latitude ?? m.data.status?.lat ?? 0,
      lon: m.data.longitude ?? m.data.status?.lng ?? 0,
    });
    if (!loc) continue;
    const online = m.data.status?.status?.toLowerCase() === 'online';
    stations.push({
      sensor_id: m.data.sn,
      label: `RF ${m.data.name ?? m.data.sn}`,
      lat: loc.lat,
      lng: loc.lng,
      state: online ? 'live' : 'offline',
      rtt_ms: null,
      latency_ms: null,
      rssi: null,
      target_count: perSensor.get(m.data.sn) ?? 0,
      last_seen: receivedAtIso,
    });
  }

  return {
    sensorSn,
    targets,
    snapshot: {
      emitter: LIVE_RF_EMITTER,
      service_id: LIVE_RF_EMITTER,
      receivedAtIso,
      online: stations.filter((s) => s.state === 'live').length,
      expected: stations.length,
      stations,
      coverage: null,
    },
  };
}

/** Course over ground from the last two distinct trajectory points; 0 when
 *  the drone has no usable history (hovering / first fix). */
function headingFromTrajectory(
  traj: ReadonlyArray<{ lat: number; lon: number }> | null | undefined,
): number {
  if (!traj || traj.length < 2) return 0;
  const to = traj[traj.length - 1]!;
  for (let i = traj.length - 2; i >= 0; i--) {
    const from = traj[i]!;
    if (from.lat === to.lat && from.lon === to.lon) continue;
    return (turfBearing([from.lon, from.lat], [to.lon, to.lat]) + 360) % 360;
  }
  return 0;
}
